import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import QuickActions from '../components/QuickActions';
import { useTeam } from '../contexts/TeamContext';
import { broadcastAPI, taskAPI, appointmentAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Plus, Calendar, CheckSquare, MessageSquare, Users, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const { currentTeam, teams, loading: teamLoading } = useTeam();
  const navigate = useNavigate();
  const [broadcasts, setBroadcasts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentTeam?.id) {
      loadDashboardData();
    }
  }, [currentTeam?.id]); // Only re-run when team ID changes

  const loadDashboardData = async () => {
    if (!currentTeam) return;
    
    try {
      // Only show loading on initial load
      if (broadcasts.length === 0 && tasks.length === 0) {
        setLoading(true);
      }
      const [broadcastsRes, tasksRes, appointmentsRes] = await Promise.all([
        broadcastAPI.getAll(currentTeam.id),
        taskAPI.getAll(currentTeam.id),
        appointmentAPI.getAll(currentTeam.id)
      ]);
      
      setBroadcasts(broadcastsRes.data.slice(0, 5));
      setTasks(tasksRes.data.filter(t => t.status !== 'completed').slice(0, 5));
      setAppointments(appointmentsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (teamLoading || !currentTeam) {
    if (!teamLoading && teams.length === 0) {
      return (
        <div className="min-h-screen flex items-center justify-center p-6">
          <Card className="p-8 text-center max-w-md glass-effect">
            <Users className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h2 className="text-2xl font-bold mb-2">No Teams Yet</h2>
            <p className="text-gray-600 mb-6">
              Create your first care team to get started.
            </p>
            <Button
              onClick={() => navigate('/teams/create')}
              className="bg-gradient-to-r from-blue-600 to-green-500"
              data-testid="create-first-team-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Team
            </Button>
          </Card>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-8 fade-in" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold mb-2">{currentTeam.name}</h1>
            <p className="text-gray-600">{currentTeam.description || 'Care coordination dashboard'}</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => navigate('/teams/create')}
              variant="outline"
              data-testid="new-team-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Team
            </Button>
            <QuickActions position="top" />
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            icon={<Calendar className="w-6 h-6" />}
            title="Appointments"
            value={appointments.length}
            color="blue"
          />
          <StatCard
            icon={<CheckSquare className="w-6 h-6" />}
            title="Active Tasks"
            value={tasks.length}
            color="green"
          />
          <StatCard
            icon={<MessageSquare className="w-6 h-6" />}
            title="Broadcasts"
            value={broadcasts.length}
            color="purple"
          />
          <StatCard
            icon={<TrendingUp className="w-6 h-6" />}
            title="Team Members"
            value="—"
            color="orange"
          />
        </div>

        {/* Recent Broadcasts */}
        <Card className="glass-effect p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Recent Updates</h2>
            <Button size="sm" variant="outline">
              View All
            </Button>
          </div>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : broadcasts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No broadcasts yet. Create your first update to keep the team informed.
            </div>
          ) : (
            <div className="space-y-4">
              {broadcasts.map(broadcast => (
                <div key={broadcast.id} className="border-l-4 border-blue-500 pl-4 py-2">
                  <h3 className="font-semibold">{broadcast.title}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{broadcast.content}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(broadcast.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Tasks & Appointments */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Tasks */}
          <Card className="glass-effect p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Active Tasks</h2>
              <Button size="sm" variant="outline">
                View All
              </Button>
            </div>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : tasks.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No active tasks. All caught up!
              </div>
            ) : (
              <div className="space-y-3">
                {tasks.map(task => (
                  <div key={task.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50">
                    <CheckSquare className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-medium">{task.title}</h4>
                      {task.due_date && (
                        <p className="text-xs text-gray-500">
                          Due: {new Date(task.due_date).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <span className={`status-badge status-${task.status.replace('_', '-')}`}>
                      {task.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Appointments */}
          <Card className="glass-effect p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Upcoming Appointments</h2>
              <Button size="sm" variant="outline">
                View All
              </Button>
            </div>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : appointments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No upcoming appointments scheduled.
              </div>
            ) : (
              <div className="space-y-3">
                {appointments.map(appt => (
                  <div key={appt.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50">
                    <Calendar className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-medium">{appt.title}</h4>
                      <p className="text-xs text-gray-500">
                        {new Date(appt.datetime).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Quick Actions - Bottom */}
        <div className="mb-8">
          <QuickActions position="bottom" />
        </div>
      </div>
    </Layout>
  );
};

const StatCard = ({ icon, title, value, color }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    purple: 'from-purple-500 to-purple-600',
    orange: 'from-orange-500 to-orange-600'
  };

  return (
    <Card className="glass-effect p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <p className="text-3xl font-bold">{value}</p>
        </div>
        <div className={`p-3 rounded-xl bg-gradient-to-br ${colorClasses[color]} text-white`}>
          {icon}
        </div>
      </div>
    </Card>
  );
};

export default Dashboard;